import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import type {
  DashboardDietOrderChange,
  DashboardTrayLineWarning,
  DashboardAdmittedResident,
  DashboardDischargedResident,
  DashboardAllergyChange,
  DashboardMealPatternNote,
  DashboardSelectiveMenu,
  DashboardLayoutSettings,
  DashboardCensusSummary,
  DashboardBirthday,
  DashboardWeightAlert,
  DashboardMealServiceStatus,
} from '../types/dashboard';
import { DEFAULT_DASHBOARD_CARDS } from '../types/dashboard';
import { useFacilityDate } from '../hooks/useFacilityDate';

const dashboardKeys = {
  all: ['dashboard'] as const,
  layout: (facilityId: number, userId: number) =>
    [...dashboardKeys.all, 'layout', facilityId, userId] as const,
  data: (facilityId: number, hours: number) =>
    [...dashboardKeys.all, 'data', facilityId, hours] as const,
  allMealPatternNotes: (facilityId: number) =>
    [...dashboardKeys.all, 'allMealPatternNotes', facilityId] as const,
  census: (facilityId: number, date?: string) =>
    date
      ? ([...dashboardKeys.all, 'census', facilityId, date] as const)
      : ([...dashboardKeys.all, 'census', facilityId] as const),
  birthdays: (facilityId: number) =>
    [...dashboardKeys.all, 'birthdays', facilityId] as const,
  weightAlerts: (facilityId: number) =>
    [...dashboardKeys.all, 'weightAlerts', facilityId] as const,
  mealServiceStatus: (facilityId: number) =>
    [...dashboardKeys.all, 'mealServiceStatus', facilityId] as const,
};

// Fetcher helpers - each returns empty array on failure (mirrors Blazor try/catch pattern).
// Errors are reported via the notification store so users see feedback.
async function fetchSafe<T>(url: string): Promise<T[]> {
  try {
    const { data } = await apiClient.get<T[]>(url);
    return data ?? [];
  } catch {
    const endpoint = url.split('/').pop()?.split('?')[0] ?? url;
    useNotificationStore.getState().addNotification(
      `Failed to load ${endpoint}`,
      'error',
    );
    return [];
  }
}

// --- Layout ---

export function useDashboardLayout(facilityId: number, userId: number) {
  return useQuery({
    queryKey: dashboardKeys.layout(facilityId, userId),
    queryFn: async (): Promise<DashboardLayoutSettings> => {
      try {
        const { data } = await apiClient.get<DashboardLayoutSettings>(
          API.dashboardLayout(facilityId, userId)
        );
        return data;
      } catch {
        // Fallback to defaults if no saved layout
        return {
          fKey_User: userId,
          cards: [...DEFAULT_DASHBOARD_CARDS],
          columnsCount: 3,
        };
      }
    },
    enabled: facilityId > 0 && userId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveDashboardLayout(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: DashboardLayoutSettings) => {
      const { data } = await apiClient.post(
        API.dashboardSaveLayout(facilityId),
        settings
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useResetDashboardLayout(facilityId: number, userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.delete(
        API.dashboardResetLayout(facilityId, userId)
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

// --- Dashboard Data (all 9 sources in parallel) ---

export interface DashboardData {
  dietOrderChanges: DashboardDietOrderChange[];
  trayLineWarnings: DashboardTrayLineWarning[];
  admittedResidents: DashboardAdmittedResident[];
  admittedGuests: DashboardAdmittedResident[];
  dischargedResidents: DashboardDischargedResident[];
  unitRoomBedChanges: DashboardDischargedResident[];
  allergyChanges: DashboardAllergyChange[];
  mealPatternNotes: DashboardMealPatternNote[];
  selectiveMenuChanges: DashboardSelectiveMenu[];
}

export function useDashboardData(facilityId: number, hours: number) {
  return useQuery({
    queryKey: dashboardKeys.data(facilityId, hours),
    queryFn: async (): Promise<DashboardData> => {
      const [
        dietOrderChanges,
        trayLineWarnings,
        admittedResidents,
        admittedGuests,
        dischargedResidents,
        unitRoomBedChanges,
        allergyChanges,
        mealPatternNotes,
        selectiveMenuChanges,
      ] = await Promise.all([
        fetchSafe<DashboardDietOrderChange>(API.dashboardDietOrderChanges(facilityId, hours)),
        fetchSafe<DashboardTrayLineWarning>(API.dashboardTrayLineWarnings(facilityId)),
        fetchSafe<DashboardAdmittedResident>(API.dashboardAdmittedResidents(facilityId, hours)),
        fetchSafe<DashboardAdmittedResident>(API.dashboardAdmittedGuests(facilityId, hours)),
        fetchSafe<DashboardDischargedResident>(API.dashboardDischargedResidents(facilityId, hours)),
        fetchSafe<DashboardDischargedResident>(API.dashboardUnitRoomBedChanges(facilityId, hours)),
        fetchSafe<DashboardAllergyChange>(API.dashboardAllergyChanges(facilityId, hours)),
        fetchSafe<DashboardMealPatternNote>(API.dashboardMealPatternNotes(facilityId, hours)),
        fetchSafe<DashboardSelectiveMenu>(API.dashboardSelectiveMenuChanges(facilityId, hours)),
      ]);

      return {
        dietOrderChanges,
        trayLineWarnings,
        admittedResidents,
        admittedGuests,
        dischargedResidents,
        unitRoomBedChanges,
        allergyChanges,
        mealPatternNotes,
        selectiveMenuChanges,
      };
    },
    enabled: facilityId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAllMealPatternNotes(facilityId: number, enabled: boolean) {
  return useQuery({
    queryKey: dashboardKeys.allMealPatternNotes(facilityId),
    queryFn: () =>
      fetchSafe<DashboardMealPatternNote>(API.dashboardAllMealPatternNotes(facilityId)),
    enabled: facilityId > 0 && enabled,
    staleTime: 2 * 60 * 1000,
  });
}

// --- New Dashboard Card Hooks ---

async function fetchSafeObject<T>(url: string, fallback: T): Promise<T> {
  try {
    const { data } = await apiClient.get<T>(url);
    return data ?? fallback;
  } catch {
    const endpoint = url.split('/').pop()?.split('?')[0] ?? url;
    useNotificationStore.getState().addNotification(
      `Failed to load ${endpoint}`,
      'error',
    );
    return fallback;
  }
}

const emptyCensus: DashboardCensusSummary = {
  totalResidents: 0, totalGuests: 0, dietTypeBreakdown: [], unitBreakdown: [],
};

const emptyMealStatus: DashboardMealServiceStatus = {
  currentMealtime: '', nextMealtime: '', menuCycleName: '', menuCycleDay: 0,
  trayTicketsPrinted: 0, trayTicketsPending: 0,
  selectiveMenusCompleted: 0, selectiveMenusTotal: 0, mealtimeBreakdown: [],
};

export function useDashboardCensus(facilityId: number) {
  // The facility's today: the guest count is guests eating that day, and the
  // date in the key refetches the card once the day changes.
  const date = useFacilityDate().today();
  return useQuery({
    queryKey: dashboardKeys.census(facilityId, date),
    queryFn: () => fetchSafeObject<DashboardCensusSummary>(
      API.dashboardCensusSummary(facilityId, date), emptyCensus
    ),
    enabled: facilityId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDashboardBirthdays(facilityId: number) {
  return useQuery({
    queryKey: dashboardKeys.birthdays(facilityId),
    queryFn: () => fetchSafe<DashboardBirthday>(
      API.dashboardUpcomingBirthdays(facilityId, 7)
    ),
    enabled: facilityId > 0,
    staleTime: 10 * 60 * 1000,
  });
}

export function useDashboardWeightAlerts(facilityId: number) {
  return useQuery({
    queryKey: dashboardKeys.weightAlerts(facilityId),
    queryFn: () => fetchSafe<DashboardWeightAlert>(
      API.dashboardWeightAlerts(facilityId)
    ),
    enabled: facilityId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDashboardMealServiceStatus(facilityId: number) {
  return useQuery({
    queryKey: dashboardKeys.mealServiceStatus(facilityId),
    queryFn: () => fetchSafeObject<DashboardMealServiceStatus>(
      API.dashboardMealServiceStatus(facilityId), emptyMealStatus
    ),
    enabled: facilityId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export interface BedsideNotOrderedPatient {
  patientId: number;
  fullName: string;
  room_No: number;
  bed_No: string;
  unitName: string;
}

export async function getNotOrderedPatients(facilityId: number, mealtime: number): Promise<BedsideNotOrderedPatient[]> {
  try {
    const { data } = await apiClient.get<BedsideNotOrderedPatient[]>(
      API.bedsideNotOrdered(facilityId, mealtime),
      { _skipSessionExpiry: true } as never,
    );
    return data ?? [];
  } catch {
    return [];
  }
}

